import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip,
  Alert, TextField, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Select, MenuItem, FormControl, InputLabel,
  IconButton, Checkbox, Tooltip, Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ShareIcon from '@mui/icons-material/Share';
import CalendarioTareas from '../components/CalendarioTareas';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  getTareas, crearTarea, actualizarTarea, completarTarea, borrarTarea, getMiEquipo,
  getAgendasVisibles, compartirAgenda, getMisAgendasCompartidas,
  revocarAgendaCompartida, getUsuariosLista
} from '../api';

// ── CONSTANTES ────────────────────────────────────────────
const PRIORIDADES: any = {
  alta:  { label: 'Alta',  color: '#ef4444' },
  media: { label: 'Media', color: '#f59e0b' },
  baja:  { label: 'Baja',  color: '#22c55e' },
};

// Agrupa tareas por situación temporal
function clasificarFecha(fechaLimite: string | null): string {
  if (!fechaLimite) return 'sin_fecha';
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const f = new Date(fechaLimite); f.setHours(0, 0, 0, 0);
  const diff = Math.round((f.getTime() - hoy.getTime()) / 86400000);
  if (diff < 0) return 'vencidas';
  if (diff === 0) return 'hoy';
  if (diff <= 7) return 'semana';
  return 'futuro';
}

const GRUPOS = [
  { key: 'vencidas', label: '⚠️ Vencidas', color: '#ef4444' },
  { key: 'hoy',      label: '📅 Hoy',       color: '#4f46e5' },
  { key: 'semana',   label: '🗓 Esta semana', color: '#0ea5e9' },
  { key: 'futuro',   label: '⏳ Más adelante', color: '#94a3b8' },
  { key: 'sin_fecha',label: '📌 Sin fecha',  color: '#64748b' },
];

export default function Tareas() {
  const [tareas, setTareas] = useState<any[]>([]);
  const [equipo, setEquipo] = useState<any[]>([]);
  const [dialog, setDialog] = useState<any>(null);
  const [verCompletadas, setVerCompletadas] = useState(false);
  const [vista, setVista] = useState<'lista' | 'calendario'>('lista');
  const [agendaDe, setAgendaDe] = useState<number>(0); // 0 = mi agenda
  const [agendasVisibles, setAgendasVisibles] = useState<any[]>([]);
  const [dialogCompartir, setDialogCompartir] = useState(false);
  const [usuariosOrg, setUsuariosOrg] = useState<any[]>([]);
  const [misCompartidas, setMisCompartidas] = useState<any[]>([]);
  const [compartirForm, setCompartirForm] = useState<any>({
    destinatario_id: '', permanente: true, fecha_inicio: '', fecha_fin: ''
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const cargar = () => {
    getTareas(agendaDe || undefined).then(setTareas).catch(() => {});
    getMiEquipo().then(setEquipo).catch(() => setEquipo([]));
    getAgendasVisibles().then(setAgendasVisibles).catch(() => setAgendasVisibles([]));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, [agendaDe]);

  // Agenda seleccionada y modo solo lectura (agendas compartidas: se ven, no se tocan)
  const agendaSel = agendasVisibles.find((a: any) => a.id === agendaDe);
  const soloLectura = agendaDe !== 0 && agendaSel?.origen === 'compartida';

  const abrirCompartir = () => {
    getUsuariosLista().then(setUsuariosOrg).catch(() => setUsuariosOrg([]));
    getMisAgendasCompartidas().then(setMisCompartidas).catch(() => setMisCompartidas([]));
    setCompartirForm({ destinatario_id: '', permanente: true, fecha_inicio: '', fecha_fin: '' });
    setDialogCompartir(true);
  };

  const enviarCompartir = async () => {
    try {
      await compartirAgenda({
        destinatario_id: compartirForm.destinatario_id,
        fecha_inicio: compartirForm.permanente ? null : compartirForm.fecha_inicio,
        fecha_fin: compartirForm.permanente ? null : compartirForm.fecha_fin,
      });
      getMisAgendasCompartidas().then(setMisCompartidas).catch(() => {});
      setCompartirForm({ destinatario_id: '', permanente: true, fecha_inicio: '', fecha_fin: '' });
      alert('Agenda compartida correctamente');
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al compartir la agenda');
    }
  };

  const revocar = async (id: number) => {
    if (!window.confirm('¿Dejar de compartir tu agenda con esta persona?')) return;
    try {
      await revocarAgendaCompartida(id);
      getMisAgendasCompartidas().then(setMisCompartidas).catch(() => {});
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  const nueva = () => setDialog({
    titulo: '', descripcion: '',
    asignado_a: (agendaDe && !soloLectura) ? agendaDe : user.id,
    fecha_limite: '', prioridad: 'media', recordatorio_fecha: ''
  });

  const guardar = async () => {
    if (!dialog.titulo) { alert('La tarea necesita un título'); return; }
    try {
      if (dialog.id) {
        await actualizarTarea(dialog.id, dialog);
      } else {
        await crearTarea(dialog);
      }
      setDialog(null);
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al guardar la tarea');
    }
  };

  const toggleCompletar = async (id: number) => {
    try { await completarTarea(id); cargar(); }
    catch (e: any) { alert(e.response?.data?.detail || 'Error'); }
  };

  const eliminar = async (id: number) => {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    try { await borrarTarea(id); cargar(); }
    catch (e: any) { alert(e.response?.data?.detail || 'Error al eliminar'); }
  };

  // Opciones de asignación: yo + mi equipo
  const opcionesAsignacion = [
    { id: user.id, nombre: user.nombre, apellidos: user.apellidos, esYo: true },
    ...equipo.filter((e: any) => e.id !== user.id),
  ];

  // Tareas activas (no completadas) agrupadas por fecha
  const activas = tareas.filter(t => t.estado !== 'completada');
  const completadas = tareas.filter(t => t.estado === 'completada');

  const tareasPorGrupo = (grupoKey: string) =>
    activas.filter(t => clasificarFecha(t.fecha_limite) === grupoKey);

  const renderTarjeta = (t: any) => {
    const prio = PRIORIDADES[t.prioridad] || PRIORIDADES.media;
    const asignadaPorOtro = t.asignado_por && t.asignado_por !== t.asignado_a;
    const meLaAsignaron = t.asignado_a === user.id && asignadaPorOtro;
    return (
      <Card key={t.id} sx={{ borderRadius: 2, mb: 1.5, borderLeft: `4px solid ${prio.color}`,
        opacity: t.estado === 'completada' ? 0.6 : 1 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Checkbox
              checked={t.estado === 'completada'}
              onChange={() => toggleCompletar(t.id)}
              disabled={soloLectura}
              sx={{ p: 0.5, mt: -0.5 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 15,
                  textDecoration: t.estado === 'completada' ? 'line-through' : 'none' }}>
                  {t.titulo}
                </Typography>
                <Chip label={prio.label} size="small"
                  sx={{ bgcolor: prio.color, color: 'white', fontWeight: 600, height: 20, fontSize: 11 }} />
              </Box>
              {t.descripcion && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t.descripcion}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {t.fecha_limite && (
                  <Typography variant="caption" color="text.secondary">📅 {t.fecha_limite}</Typography>
                )}
                {t.recordatorio_fecha && (
                  <Tooltip title={`Recordatorio: ${t.recordatorio_fecha}`}>
                    <NotificationsActiveIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                  </Tooltip>
                )}
                {/* Si la tarea es para otra persona (la asigné yo) */}
                {t.asignado_a !== user.id && (
                  <Chip label={`Para: ${t.asignado_nombre} ${t.asignado_apellidos || ''}`}
                    size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                )}
                {/* Si me la asignó otra persona */}
                {meLaAsignaron && (
                  <Chip label={`De: ${t.creador_nombre} ${t.creador_apellidos || ''}`}
                    size="small" variant="outlined" color="primary" sx={{ height: 20, fontSize: 11 }} />
                )}
              </Box>
            </Box>
            {!soloLectura && (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <IconButton size="small" onClick={() => setDialog({ ...t })}>
                  <EditIcon fontSize="small" />
                </IconButton>
                {(t.asignado_por === user.id || user.rol === 'admin') && (
                  <IconButton size="small" color="error" onClick={() => eliminar(t.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', md: '2rem' } }}>
          📋 Agenda de tareas
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {agendasVisibles.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Ver agenda de</InputLabel>
              <Select value={agendaDe} label="Ver agenda de"
                onChange={(e) => setAgendaDe(Number(e.target.value))}>
                <MenuItem value={0}>👤 Mi agenda</MenuItem>
                {agendasVisibles.map((a: any) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.origen === 'equipo' ? '👥' : '🔗'} {a.nombre} {a.apellidos || ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button variant="outlined" startIcon={<ShareIcon />} onClick={abrirCompartir}>
            Compartir
          </Button>
          <ToggleButtonGroup
            value={vista} exclusive size="small"
            onChange={(_, v) => { if (v) setVista(v); }}
          >
            <ToggleButton value="lista">📋 Lista</ToggleButton>
            <ToggleButton value="calendario">📅 Calendario</ToggleButton>
          </ToggleButtonGroup>
          {!soloLectura && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={nueva}
              sx={{ bgcolor: '#4f46e5' }}>
              Nueva tarea
            </Button>
          )}
        </Box>
      </Box>
      
      {/* VISTA CALENDARIO */}
      {vista === 'calendario' && (
        <CalendarioTareas
          tareas={tareas}
          onSelectTarea={(t) => { if (!soloLectura) setDialog({ ...t }); }}
          onSelectDia={(fecha) => {
            if (soloLectura) return;
            setDialog({
              titulo: '', descripcion: '',
              asignado_a: (agendaDe && !soloLectura) ? agendaDe : user.id,
              fecha_limite: fecha, prioridad: 'media', recordatorio_fecha: ''
            });
          }}
        />
      )}

      {/* VISTA LISTA */}
      {vista === 'lista' && (
        <>
      {activas.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>No tienes tareas pendientes. ¡Crea una nueva!</Alert>
      )}

      {/* Grupos por fecha */}
      {GRUPOS.map(grupo => {
        const tareasGrupo = tareasPorGrupo(grupo.key);
        if (tareasGrupo.length === 0) return null;
        return (
          <Box key={grupo.key} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography sx={{ fontWeight: 700, color: grupo.color, fontSize: 15 }}>
                {grupo.label}
              </Typography>
              <Chip label={tareasGrupo.length} size="small"
                sx={{ height: 20, fontSize: 11, bgcolor: grupo.color, color: 'white' }} />
            </Box>
            {tareasGrupo.map(renderTarjeta)}
          </Box>
        );
      })}

      {/* Completadas (plegable) */}
      {completadas.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 2 }} />
          <Button variant="text" onClick={() => setVerCompletadas(!verCompletadas)}
            sx={{ mb: 1 }}>
            {verCompletadas ? '▼' : '▶'} Completadas ({completadas.length})
          </Button>
          {verCompletadas && completadas.map(renderTarjeta)}
        </Box>
      )}
      </>
      )}

      {/* DIALOG CREAR / EDITAR */}
      <Dialog open={!!dialog} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setDialog(null); } }} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog?.id ? 'Editar tarea' : 'Nueva tarea'}</DialogTitle>
        <DialogContent>
          {dialog && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label="Título *" value={dialog.titulo}
                onChange={(e) => setDialog({ ...dialog, titulo: e.target.value })} />
              <TextField label="Descripción" multiline rows={2} value={dialog.descripcion || ''}
                onChange={(e) => setDialog({ ...dialog, descripcion: e.target.value })} />

              {/* Asignar a (yo o mi equipo) */}
              <FormControl fullWidth>
                <InputLabel>Asignar a</InputLabel>
                <Select value={dialog.asignado_a} label="Asignar a"
                  onChange={(e) => setDialog({ ...dialog, asignado_a: e.target.value })}
                  disabled={!!dialog.id}>
                  {opcionesAsignacion.map((o: any) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.esYo ? '👤 Yo' : `${o.nombre} ${o.apellidos || ''}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="Fecha límite" type="date" InputLabelProps={{ shrink: true }} fullWidth
                  value={dialog.fecha_limite || ''}
                  onChange={(e) => setDialog({ ...dialog, fecha_limite: e.target.value })} />
                <FormControl fullWidth>
                  <InputLabel>Prioridad</InputLabel>
                  <Select value={dialog.prioridad} label="Prioridad"
                    onChange={(e) => setDialog({ ...dialog, prioridad: e.target.value })}>
                    <MenuItem value="alta">Alta</MenuItem>
                    <MenuItem value="media">Media</MenuItem>
                    <MenuItem value="baja">Baja</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <TextField label="Recordatorio por email (fecha y hora)" type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={dialog.recordatorio_fecha ? dialog.recordatorio_fecha.replace(' ', 'T').slice(0,16) : ''}
                onChange={(e) => setDialog({ ...dialog, recordatorio_fecha: e.target.value ? e.target.value.replace('T', ' ') + ':00' : '' })}
                helperText="Se enviará un email al asignado en esa fecha y hora" />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancelar</Button>
          <Button onClick={guardar} variant="contained" sx={{ bgcolor: '#4f46e5' }}
            disabled={!dialog?.titulo}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG COMPARTIR AGENDA */}
      <Dialog open={dialogCompartir} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setDialogCompartir(false); } }} maxWidth="sm" fullWidth>
        <DialogTitle>🔗 Compartir mi agenda</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Compartir con</InputLabel>
              <Select value={compartirForm.destinatario_id} label="Compartir con"
                onChange={(e) => setCompartirForm({ ...compartirForm, destinatario_id: e.target.value })}>
                {usuariosOrg.filter((u: any) => u.id !== user.id).map((u: any) => (
                  <MenuItem key={u.id} value={u.id}>{u.nombre} {u.apellidos || ''}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Duración</InputLabel>
              <Select value={compartirForm.permanente ? 'permanente' : 'periodo'} label="Duración"
                onChange={(e) => setCompartirForm({ ...compartirForm, permanente: e.target.value === 'permanente' })}>
                <MenuItem value="permanente">Permanentemente</MenuItem>
                <MenuItem value="periodo">Desde el día... hasta...</MenuItem>
              </Select>
            </FormControl>

            {!compartirForm.permanente && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="Desde" type="date" InputLabelProps={{ shrink: true }} fullWidth
                  value={compartirForm.fecha_inicio}
                  onChange={(e) => setCompartirForm({ ...compartirForm, fecha_inicio: e.target.value })} />
                <TextField label="Hasta" type="date" InputLabelProps={{ shrink: true }} fullWidth
                  value={compartirForm.fecha_fin}
                  onChange={(e) => setCompartirForm({ ...compartirForm, fecha_fin: e.target.value })} />
              </Box>
            )}

            <Button variant="contained" onClick={enviarCompartir} sx={{ bgcolor: '#4f46e5' }}
              disabled={!compartirForm.destinatario_id ||
                (!compartirForm.permanente && (!compartirForm.fecha_inicio || !compartirForm.fecha_fin))}>
              Compartir agenda
            </Button>

            {misCompartidas.length > 0 && (
              <>
                <Divider />
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Compartida actualmente con:</Typography>
                {misCompartidas.map((c: any) => (
                  <Box key={c.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">
                      {c.destinatario_nombre} {c.destinatario_apellidos || ''} — {c.fecha_inicio ? `${c.fecha_inicio} → ${c.fecha_fin}` : 'permanente'}
                    </Typography>
                    <IconButton size="small" color="error" onClick={() => revocar(c.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogCompartir(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}